import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import KeyFigure from "../../../models/keyFigure/KeyFigure.js";
import KeyFigureResult from "../../../models/keyFigure/KeyFigureResult.js";
import KeyFigureValue from "../../../models/keyFigure/value/KeyFigureValue.js";
import session from "../../../core/session.js";

/**
 * The `KeyFigureCalculationService` is responsible for querying keyfigure
 * results based on the formula tree of a keyfigure.
 *
 * @param {string} companyId - The identifier for the company the
 *   service should compute data for
 * @param {sync} sync - An object with a put() method doing PUT
 *   requests
 */
export default class KeyFigureCalculationService {
	constructor({ companyId }) {
		this._companyId = companyId;

		this._repository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/KeyFigure/Calculate",
			defaultFilters: { companyId: this._companyId },
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	async fetchKeyFigureResult({ periods, keyFigure, dimensionId }) {
		const data = await this._repository.update({
			model: {
				periodType: periods[0].getPeriodType(),
				periodIds: periods.map((p) => p.toString()).join(","),
				keyFigureJson: keyFigure.serialize(),
				// Do not cache the keyfigure result on the server, as the data
				// (formula definition) is subject to further changes and we do
				// not want to get back cached data.
				noCache: true,
			},
			filter: dimensionId && { dimensionId },
			notify: false,
		});

		return this._materializeKeyFigureResult({ data, keyFigure, periods });
	}

	/**
	 * Calculate and return values for `formulaNodes` over `periods`.
	 *
	 * @param {AbstractFormulaNodes[]} formulaNodes - The formulas for
	 *   which to calculate values. These formulas will be the keys of
	 *   the returned map
	 * @param {Period[]} periods - The periods over which to calculate
	 *   values. These periods will be the keys of the return value's
	 *   values
	 * @param {string} [dimenionId] - A dimension used to restrict
	 *   the transactions taken into account to do the calculation
	 * @return {Map<AbstractFormulaNode, Map<Period, KeyFigureValue>>}
	 */
	async fetchResultsForFormulaNodes({ formulaNodes, periods, dimensionId }) {
		const results = await Promise.all(
			formulaNodes.map((formulaNode) =>
				this.fetchKeyFigureResult({
					periods,
					keyFigure: new KeyFigure({
						formula: formulaNode,
						valueType: formulaNode.getValueType(),
					}),
					dimensionId,
				}),
			),
		);

		return this._convertNodeValueResultsIntoMap(results);
	}

	_materializeKeyFigureResult({ data, keyFigure, periods }) {
		return new KeyFigureResult({
			values: data.map(
				(datum) =>
					new KeyFigureValue({
						period: periods.find(
							(p) => p.toString() === datum.periodId,
						),
						value: datum.value,
						component: keyFigure,
					}),
			),
			keyFigures: [keyFigure],
			periods,
		});
	}

	/**
	 * Convert an array of `KeyFigureResult` into a map with formula
	 * nodes as keys and period maps as values. A period map has a
	 * period as key and a KeyFigureValue as value.
	 *
	 * @param {KeyFigureResult[]} results - The values to convert
	 * @return {Map<AbstractFormulaNode, Map<Period, KeyFigureValue>>}
	 */
	_convertNodeValueResultsIntoMap(results) {
		const nodeValueMap = new Map();

		for (const result of results) {
			nodeValueMap.set(
				result.getFirstKeyFigure().getFormula(),
				result.asPeriodMap(),
			);
		}

		return nodeValueMap;
	}
}
